import React, { useState, useEffect } from 'react';
import { Select, Spin } from 'antd';
import api from 'api';

const Option = Select.Option;

// Contract dropdown for a work order: the selected site's ACTIVE contracts plus the global
// DEFAULT contract, which is always offered (Henry, 2026-10-31) so a site with no contract
// can still be booked. The list comes from the resolveSiteContracts RPC -- the same call
// workOrders.create.createMethod uses -- so the "default" rule (contract_type = 'default',
// no site) lives in one place on the back end.
//
// On a new work order (`values` is null) the field is prefilled with the RPC's `resolved`
// pick: the only active site contract, or DEFAULT when the site has none; several site
// contracts leave it blank for the call-taker to choose. Changing the site re-resolves.
// On an existing work order the saved contract is never changed automatically and stays in
// the list even if it has since expired.
//
// getFieldDecorator injects `value` (contract_id) and `onChange`; `form` is needed to read
// the live site_id.
function contractLabel(c, isDefault) {
    return isDefault
        ? `${c.contract_number} (global default)`
        : `${c.contract_number} (${c.contract_type})`;
}

function SiteContract({ value, onChange, item, values, form }) {
    const siteFieldKey = item.siteFieldKey || 'site_id';
    const createMode = !values;
    const siteId = form ? form.getFieldValue(siteFieldKey) : (values ? values[siteFieldKey] : null);

    const [options, setOptions] = useState([]);
    const [fetching, setFetching] = useState(false);

    useEffect(() => {
        if (!siteId) {
            setOptions([]);
            if (createMode && value) onChange(undefined);
            return;
        }

        let cancelled = false;
        setFetching(true);
        api.rpc('resolveSiteContracts', { site_id: siteId })
            .then(resp => {
                if (cancelled) return;
                const result = (resp && resp.body && resp.body.result) || {};
                const siteContracts = result.site_contracts || [];
                const defaultContract = result.default_contract;

                const list = siteContracts.map(c => ({ value: c.id, label: contractLabel(c, false) }));
                if (defaultContract && !list.some(o => o.value === defaultContract.id)) {
                    list.push({ value: defaultContract.id, label: contractLabel(defaultContract, true) });
                }

                if (!createMode && value && !list.some(o => o.value === value)) {
                    list.unshift({ value, label: values.contract_number || value });
                }

                setOptions(list);
                setFetching(false);

                if (createMode && !list.some(o => o.value === value)) {
                    onChange(result.resolved ? result.resolved.id : undefined);
                }
            })
            .catch(() => {
                if (cancelled) return;
                setOptions([]);
                setFetching(false);
            });

        return () => { cancelled = true; };
    }, [siteId]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Select
            value={value || undefined}
            onChange={onChange}
            allowClear
            disabled={fetching}
            placeholder={siteId ? 'Choose a contract' : 'Pick a site first'}
            notFoundContent={fetching ? <Spin size="small" /> : null}>
            {options.map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}
        </Select>
    );
}

export default SiteContract;
